import { Directive, ElementRef, HostBinding, HostListener, Input, OnChanges, OnInit, Renderer2, SimpleChanges } from '@angular/core';

@Directive({
    selector: '[hamIconShapes]',
})
export class HamIconShapesDirective implements OnInit, OnChanges {
    @Input() size: string = 'xs'; // Default size
    @Input() color: string = '#000'; // Default color
    @Input() type: string = 'circle'; // Default type
    @Input() icon: string | null = null; // Default icon
    @Input() variant:
        | 'primary'
        | 'gray'
        | 'red'
        | 'red-transparent'
        | 'teal'
        | 'dark-red'
        | 'dark-green'
        | 'yellow'
        | 'indigo' = 'primary'; // Default variant
    @Input() mustBubbleEvent: boolean = false; // Default false
    @Input() disabled: boolean = false; // New disabled property
    @Input() keepOriginalColors: boolean = false; // Keep Icon as is
    @Input() noHover: boolean = false; // New noHover property


    @HostBinding('class') get className(): string {
        const baseClass = 'ham-icon-shapes';
        const sizeClass = this.size;
        const typeClass = this.type;
        const variantClass = this.variant;

        // Combine the classes dynamically
        return `${baseClass} ${sizeClass} ${typeClass} ${variantClass}${this.disabled ? ' disabled' : ''}${this.noHover ? ' no-hover' : ''}`;
    }

    @HostListener('click', ['$event'])
    handleClick(event: MouseEvent) {
        if (this.disabled) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }
        if (!this.mustBubbleEvent) {
            event.stopPropagation();
        }
    }

    constructor(private el: ElementRef, private renderer: Renderer2) { }

    ngOnInit() {
        // Create an inner <i> element to apply styles to
        const iconElement = this.renderer.createElement('i');

        iconElement.classList.add(this.variant);

        // Append the created <i> element to the host element (span)
        this.renderer.appendChild(this.el.nativeElement, iconElement);

        // Apply styles to the inner <i> element
        this.applyStyles(iconElement);
    }

    // Add this to your class
    ngOnChanges(changes: SimpleChanges) {
        if (changes['variant']) {
            // Logic to update the inner <i> element's class
            const newVariant = changes['variant'].currentValue;
            const oldVariant = changes['variant'].previousValue;

            // Find the inner <i> element (assuming it's the first child)
            const iconElement = this.el.nativeElement.querySelector('i');

            if (iconElement && oldVariant) {
                this.renderer.removeClass(iconElement, oldVariant);
            }
            if (iconElement && newVariant) {
                this.renderer.addClass(iconElement, newVariant);
            }
        }
    }

    private applyStyles(iconElement: HTMLElement) {
        // Set the size of the icon element
        this.renderer.setStyle(iconElement, 'width', this.size);
        this.renderer.setStyle(iconElement, 'height', this.size);

        // SVG as an IMG -> Same color as downloaded (needs variant to be an empty string as well)
        if (this.keepOriginalColors && this.icon) {
            /* Keep the SVG exactly as delivered (multi‑colour, gradients, etc.)
               by painting it as a background image instead of using the mask. */
            this.renderer.setStyle(iconElement, 'background-image', `url(${this.icon})`);
            this.renderer.setStyle(iconElement, 'background-size', 'contain');
            this.renderer.setStyle(iconElement, 'background-repeat', 'no-repeat');
            this.renderer.setStyle(iconElement, 'background-position', 'center');
        }
        // Set icon mask style if an icon is provided and recolouring is wanted
        else if (this.icon) {
            this.renderer.setStyle(iconElement, '-webkit-mask-image', `url(${this.icon})`);
            this.renderer.setStyle(iconElement, 'mask-image', `url(${this.icon})`);
        }

        // Apply shape type
        if (this.type === 'circle') {
            this.el.nativeElement.classList.add('circle');
        } else if (this.type === 'square') {
            this.el.nativeElement.classList.add('square');
        } else if (this.type === 'rounded') {
            this.el.nativeElement.classList.add('rounded');
        }
        this.el.nativeElement.classList.add(this.size);
        this.el.nativeElement.classList.add(this.variant);
    }
}
